<template>
  <div class="jiangguowang-recommend-card" @click="addJiangguowangConfig">
    <div class="jiangguowang-image-container">
      <img src="@/assets/jiangguowang1.webp" alt="jiangguowang" class="jiangguowang-main-img" />
    </div>

    <!-- 光晕层 -->
    <div class="jiangguowang-glow-layer"></div>

    <!-- 关闭按钮 -->
    <button class="jiangguowang-close-btn" @click.stop="emit('close')">
      <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
        <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
      </svg>
    </button>

    <!-- 推荐徽章（左上角） -->
    <div class="jiangguowang-minimal-badge">
      <span class="dot"></span>
      <span class="label">{{ t('jiangguowang_recommend') }}</span>
    </div>

    <!-- 直播间徽章（右下角） -->
    <div class="jiangguowang-live-badge" @click.stop="openDouyinLive">
      <span class="live-pulse"></span>
      <span class="live-label">{{ t('jiangguowang_live') }}</span>
      <svg width="9" height="9" viewBox="0 0 16 16" fill="none">
        <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </div>

    <div class="jiangguowang-hover-overlay">
      <div class="overlay-content">
        <!-- 导入配置 -->
        <div class="overlay-action-btn" @click.stop="addJiangguowangConfig">
          <div class="action-icon-wrapper">
            <SvgAsset :content="actionDownloadSvg" />
            <div class="icon-glow"></div>
          </div>
          <span class="action-text">{{ t('one_click_export_config') }}</span>
        </div>

        <!-- 分隔线 -->
        <div class="overlay-divider"></div>

        <!-- 进入直播间 -->
        <div class="overlay-action-btn live-action-btn" @click.stop="openDouyinLive">
          <div class="action-icon-wrapper">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="2.5" fill="currentColor" />
              <path d="M7 7a7.07 7.07 0 0 0 0 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              <path d="M17 7a7.07 7.07 0 0 1 0 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              <path d="M9.5 9.5a3.5 3.5 0 0 0 0 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              <path d="M14.5 9.5a3.5 3.5 0 0 1 0 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
            <div class="icon-glow live-glow"></div>
          </div>
          <span class="action-text">{{ t('jiangguowang_live') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { configSet } from '@/service/driver/config_set'
import { DeviceManager } from '@/service/device'
import { DevicePid } from '@/define/define'
import { PRESET_CONFIGS } from '@/define/presets'
import { trackEvent } from '@/activitylog/activitylog'
import { openExternalLink } from '@/tool/utility'
import SvgAsset from '../common/SvgAsset.vue'
import actionDownloadSvg from '@/assets/inline-svg/action-download.svg?raw'
import i18n from '@/languages/i18n'

const { t } = i18n.global

const emit = defineEmits<{
  /** 配置已满时，需要父组件打开导入替换对话框 */
  'need-import-replace': [title: string, content: string]
  /** 需要父组件弹出替换槽位对话框，参数为新配置在 configList 中的 index */
  'need-replace-slot': [index: number]
  /** 需要父组件将配置列表滚动到底部 */
  'scroll-to-bottom': []
  /** 用户点击关闭按钮，请求父组件隐藏此卡片 */
  close: []
}>()

const openDouyinLive = () => {
  openExternalLink('https://www.douyin.com/user/MS4wLjABAAAAPK5z9MQKUH0nFnB-_WQuDeecZp3RKw6XxA_q-eJYQbM')
}

const trackAddPresetConfig = async () => {
  if (import.meta.env.PROD == false) return
  if (i18n.global.locale == 'zh') {
    trackEvent('add_jiangguowang_config')
  }
}

const JIANGGUOWANG_PRESET_MAP: Partial<Record<DevicePid, string>> = {
  [DevicePid.IROKRA68]: PRESET_CONFIGS.JINGTAIVA_RA68_JIANGGUOWANG,
  [DevicePid.IROKRA68_1]: PRESET_CONFIGS.HUOCHAIREN_RA68_JIANGGUOWANG,
  [DevicePid.IROKMG75V2C12]: PRESET_CONFIGS.HUOCHAIREN_MG75V2_JIANGGUOWANG,
}

const addJiangguowangConfig = async () => {
  const currentPid = DeviceManager.instance.state.currentPid
  const presetContent = JIANGGUOWANG_PRESET_MAP[currentPid] ?? PRESET_CONFIGS.HUOCHAIREN_RA68_JIANGGUOWANG
  // 配置已满时，与导入逻辑保持一致：弹出替换对话框让用户选择覆盖哪个配置
  if (configSet.isOnboardConfigSupported && configSet.configList.value.length >= configSet.maxConfigCount) {
    emit('need-import-replace', PRESET_CONFIGS.TITLT, presetContent.trim())
    return
  }
  await configSet.setConfigPreset(PRESET_CONFIGS.TITLT, presetContent)
  const newIndex = configSet.configList.value.length - 1
  trackAddPresetConfig()
  emit('scroll-to-bottom')
  if (configSet.isOnboardConfigSupported) {
    // 板载设备：新导入的预设是存档配置（无槽位），必须通过替换槽位对话框绑定才能切换
    emit('need-replace-slot', newIndex)
  } else {
    await configSet.apiChangeConfig(newIndex)
  }
}
</script>

<style lang="less" scoped>
/* 讲过往 推荐配置卡片 */
.jiangguowang-recommend-card {
  position: relative;
  bottom: auto;
  cursor: pointer;
  margin-top: 10px;
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  border-radius: var(--radius-l);
  flex-shrink: 0;
  overflow: hidden;

  .jiangguowang-image-container {
    flex: 1;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    min-height: 0;
    transition: transform 0.6s var(--transition-base);
  }

  .jiangguowang-main-img {
    max-width: 100%;
    object-fit: cover;
    transform: scale(1.01);
    transition: transform 0.8s cubic-bezier(0.165, 0.84, 0.44, 1);
  }

  .jiangguowang-close-btn {
    position: absolute;
    top: 10px;
    left: 10px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.25);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 50%;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    z-index: 5;
    opacity: 0;
    transform: scale(0.85);
    transition: all 0.3s var(--transition-base);

    &:hover {
      background: rgba(0, 0, 0, 0.5);
      color: #fff;
      transform: scale(1) rotate(90deg);
      border-color: rgba(255, 255, 255, 0.35);
    }
  }

  .jiangguowang-glow-layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 50% 120%, rgba(var(--color-brand-1-rgb), 0.15), transparent 70%);
    pointer-events: none;
    z-index: 1;
  }

  .jiangguowang-minimal-badge {
    position: absolute;
    top: 14px;
    right: 14px;
    padding: 5px 10px;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    display: flex;
    align-items: center;
    gap: 6px;
    z-index: 3;
    transition: all 0.4s var(--transition-base);

    .dot {
      width: 4px;
      height: 4px;
      background: var(--brand-color-pure);
      border-radius: 50%;
      box-shadow: 0 0 8px var(--brand-color-pure);
      animation: dot-pulse 2s infinite;
    }

    .label {
      color: #fff;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.8px;
      text-transform: uppercase;
    }
  }

  .jiangguowang-live-badge {
    position: absolute;
    bottom: 14px;
    right: 14px;
    padding: 5px 10px;
    background: rgba(255, 40, 70, 0.25);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 100, 120, 0.35);
    border-radius: 6px;
    display: flex;
    align-items: center;
    gap: 6px;
    z-index: 3;
    cursor: pointer;
    transition: all 0.4s var(--transition-base);

    &:hover {
      background: rgba(255, 40, 70, 0.45);
      transform: translateY(-2px);
      border-color: rgba(255, 100, 120, 0.6);
    }

    .live-pulse {
      width: 5px;
      height: 5px;
      background: #ff3355;
      border-radius: 50%;
      box-shadow: 0 0 6px #ff3355;
      animation: dot-pulse 1.5s infinite;
      flex-shrink: 0;
    }

    .live-label {
      color: #fff;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.8px;
    }

    svg {
      color: rgba(255, 255, 255, 0.75);
    }
  }

  .jiangguowang-hover-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.42);
    backdrop-filter: blur(3px) saturate(120%);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: all 0.5s var(--transition-base);
    z-index: 4;

    .overlay-content {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 12px;
      transform: translateY(10px);
      transition: transform 0.5s var(--transition-base);
      padding: 0 12px;

      .overlay-action-btn {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        cursor: pointer;
        padding: 14px 16px;
        border-radius: var(--radius-m);
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px);
        transition: all 0.3s var(--transition-base);
        flex: 1;

        &:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
        }

        &.live-action-btn {
          background: rgba(255, 40, 70, 0.2);
          border-color: rgba(255, 100, 120, 0.3);

          &:hover {
            background: rgba(255, 40, 70, 0.35);
          }
        }
      }

      .action-icon-wrapper {
        position: relative;
        width: 50px;
        height: 50px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;

        .svg_asset,
        svg {
          width: 28px;
          height: 28px;
          z-index: 1;
        }

        .icon-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 40px;
          height: 40px;
          background: var(--brand-color-pure);
          border-radius: 50%;
          filter: blur(15px);
          opacity: 0.5;
        }

        .live-glow {
          background: #ff3355;
        }
      }

      .action-text {
        color: #fff;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.3px;
        white-space: normal;
        word-break: break-all;
        text-align: center;
        line-height: 1.3;
      }

      .overlay-divider {
        width: 1px;
        height: 60px;
        background: rgba(255, 255, 255, 0.15);
        border-radius: 1px;
        flex-shrink: 0;
      }
    }
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);

    .jiangguowang-close-btn {
      opacity: 1;
      transform: scale(1);
    }

    .jiangguowang-minimal-badge {
      opacity: 0;
      transform: translateY(-5px);
    }

    .jiangguowang-main-img {
      transform: scale(1.1);
    }

    .jiangguowang-live-badge {
      opacity: 0;
      transform: translateY(5px);
      pointer-events: none;
    }

    .jiangguowang-hover-overlay {
      opacity: 1;

      .overlay-content {
        transform: translateY(0);
      }
    }
  }
}

@keyframes dot-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(0.7);
  }
}
</style>
